import React from "react";
import { useNavigate } from "react-router-dom";
import { Check, Download } from "lucide-react";
import { fmt } from "@/lib/catalog";

export default function OrderSuccess({ order }) {
  const nav = useNavigate();
  const items = (order.items || []).map((i) => ({
    name: i.name, qty: i.qty, unitPrice: i.unitPrice,
    subtotal: i.subtotal != null ? i.subtotal : i.unitPrice * i.qty,
  }));
  const total = order.total != null ? order.total : items.reduce((s, i) => s + i.subtotal, 0);
  const paid = order.payStatus === "paid";
  const payLabel = order.payMethodLabel
    || (order.payMethod === "online" ? "دفع إلكتروني (Stripe)"
      : order.payMethod === "wallet" ? "المحفظة (الرصيد)"
      : order.payMethod && order.payMethod.startsWith("pm_") ? "تحويل بنكي"
      : "الدفع عند الاستلام");

  const download = () => {
    const lines = [
      "كزيوني — إثبات الطلب",
      "========================",
      "رقم الطلب: " + order.orderId,
      "التاريخ: " + new Date().toLocaleString("ar-LY"),
      "الهاتف: " + (order.phone || ""),
      "البريد الإلكتروني: " + (order.email || ""),
      "مدينة التسليم: " + (order.city || ""),
      "طريقة الدفع: " + payLabel,
      "الحالة: " + (paid ? "مدفوع" : order.payMethod === "online" ? "بانتظار الدفع" : "دفع عند الاستلام"),
      "------------------------",
      ...items.map((i) => `${i.name} × ${i.qty} — ${fmt(i.subtotal)} د.ل`),
      "------------------------",
      "الإجمالي: " + fmt(total) + " د.ل",
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = order.orderId + ".txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div dir="rtl" className="k-rise">
      <div className="text-center pt-14 px-8">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "var(--k-emerald-tint)", color: "var(--k-emerald)" }}>
          <Check size={32} strokeWidth={2.6} />
        </div>
        <h2 className="font-head text-[19px] font-bold mb-1.5">تم إنشاء طلبك بنجاح</h2>
        <p className="text-[12.5px] leading-relaxed mb-4" style={{ color: "var(--k-ink-soft)" }}>
          سيتم إرسال تفاصيل الطلب إلى {order.phone} و {order.email} (محاكاة)
        </p>
        <div data-testid="order-id" className="font-num inline-block rounded-xl px-4 py-2.5 text-[13px] font-bold mb-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          {order.orderId}
        </div>
        <div>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold" style={{ background: paid ? "var(--k-emerald-tint)" : "var(--k-gold-tint)", color: paid ? "var(--k-emerald)" : "var(--k-gold-deep)" }}>
            <Check size={13} /> {paid ? "مدفوع" : order.payMethod === "online" ? "بانتظار الدفع" : "دفع عند الاستلام"}
          </span>
        </div>
      </div>

      <div className="rounded-2xl mx-4 mt-5 p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
        <p className="text-[12px] font-bold mb-2" style={{ color: "var(--k-ink-soft)" }}>إثبات الطلب — {order.orderId}</p>
        {items.map((i, k) => (
          <div key={k} className="flex justify-between text-[12.5px] py-1" style={{ color: "var(--k-ink-soft)" }}>
            <span>{i.name} × {i.qty}</span>
            <span className="font-num">{fmt(i.subtotal)} د.ل</span>
          </div>
        ))}
        <div className="flex justify-between text-[14px] font-bold pt-2.5 mt-1.5" style={{ borderTop: "1px solid var(--k-line)" }}>
          <span>الإجمالي</span><span className="font-num">{fmt(total)} د.ل</span>
        </div>
        <div className="flex justify-between text-[12.5px] pt-1.5" style={{ color: "var(--k-ink-soft)" }}>
          <span>طريقة الدفع</span><span>{payLabel}</span>
        </div>
      </div>

      <div className="px-4 mt-4">
        <button data-testid="download-receipt-btn" onClick={download} className="k-tap w-full py-3 rounded-2xl font-bold text-[13.5px] flex items-center justify-center gap-2" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <Download size={16} /> تنزيل إثبات الطلب
        </button>
      </div>

      <p className="text-[11px] mx-4 my-4 rounded-xl px-3 py-2.5 text-center leading-relaxed" style={{ color: "var(--k-ink-soft)", background: "var(--k-surface)", border: "1px dashed var(--k-line)" }}>
        إرسال البريد/الرسالة النصية محاكاة فقط. تنزيل الإيصال أعلاه حقيقي وفعلي.
      </p>

      <div className="px-4 pb-8">
        <button data-testid="back-home-btn" onClick={() => nav("/")} className="k-tap w-full py-3.5 rounded-2xl font-bold text-[14.5px] text-white" style={{ background: "var(--k-ink)" }}>العودة للرئيسية</button>
      </div>
    </div>
  );
}
